import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BASE_URL } from '../config/constants';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';
import '../styles/player.css';

function Player() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get('courseId');
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeModule, setActiveModule] = useState(0);
  const [activeLesson, setActiveLesson] = useState(0);
  const [completedLessons, setCompletedLessons] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseData, setCourseData] = useState(null);
  const [expandedModules, setExpandedModules] = useState(new Set([0])); // First module expanded by default
  const [loadingModules, setLoadingModules] = useState(new Set());
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);

  useEffect(() => {
    if (courseId) {
      fetchCourseData();
    } else {
      setError('No course ID provided');
      setLoading(false);
    }
  }, [courseId]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  const fetchCourseData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch course details
      const courseResponse = await fetch(`${BASE_URL}/courses/${courseId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        }
      });

      if (!courseResponse.ok) {
        throw new Error('Failed to fetch course details');
      }

      const courseResult = await courseResponse.json();
      
      if (!courseResult.success) {
        throw new Error('Failed to load course details');
      }

      // Fetch modules for the course
      const modulesResponse = await fetch(`${BASE_URL}/courses/${courseId}/modules`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        }
      });

      if (!modulesResponse.ok) {
        throw new Error('Failed to fetch modules');
      }

      const modulesResult = await modulesResponse.json();
      
      if (!modulesResult.success) {
        throw new Error('Failed to load modules');
      }

      // Initialize modules without lessons - lessons will be loaded on demand
      const modulesWithoutLessons = modulesResult.data.map(module => ({
        ...module,
        lessons: null, // null means not loaded yet, empty array means no lessons
        lessonsLoaded: false
      }));

      setCourseData({
        id: courseResult.data.id,
        title: courseResult.data.title,
        code: courseResult.data.course_code,
        instructor: courseResult.data.instructor_id,
        modules: modulesWithoutLessons
      });

      // Load lessons for the first module by default
      if (modulesWithoutLessons.length > 0) {
        await fetchModuleLessons(0, modulesWithoutLessons);
      }

    } catch (err) {
      console.error('Error fetching course data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchModuleLessons = async (moduleIndex, currentModules = null) => {
    const modules = currentModules || courseData?.modules;
    if (!modules || !modules[moduleIndex]) return;

    const module = modules[moduleIndex];
    
    // If lessons already loaded, skip
    if (module.lessonsLoaded) return;

    setLoadingModules(prev => new Set([...prev, moduleIndex]));

    try {
      const lessonsResponse = await fetch(`${BASE_URL}/courses/modules/${module.id}/lessons`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        }
      });

      if (!lessonsResponse.ok) {
        console.error(`Failed to fetch lessons for module ${module.id}`);
        return;
      }

      const lessonsResult = await lessonsResponse.json();
      
      // Update the specific module with lessons
      setCourseData(prevData => {
        if (!prevData) return prevData;
        
        const updatedModules = [...prevData.modules];
        updatedModules[moduleIndex] = {
          ...updatedModules[moduleIndex],
          lessons: lessonsResult.success ? lessonsResult.data : [],
          lessonsLoaded: true
        };

        return {
          ...prevData,
          modules: updatedModules
        };
      });

    } catch (err) {
      console.error(`Error fetching lessons for module ${moduleIndex}:`, err);
    } finally {
      setLoadingModules(prev => {
        const newSet = new Set(prev);
        newSet.delete(moduleIndex);
        return newSet;
      });
    }
  };

  const handleModuleToggle = async (moduleIndex) => {
    const isExpanded = expandedModules.has(moduleIndex);
    
    setExpandedModules(prev => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.delete(moduleIndex);
      } else {
        newSet.add(moduleIndex);
      }
      return newSet;
    });

    // Load lessons if not already loaded and module is being expanded
    if (!isExpanded && courseData?.modules[moduleIndex] && !courseData.modules[moduleIndex].lessonsLoaded) {
      await fetchModuleLessons(moduleIndex);
    }
  };

  const handleLessonClick = async (moduleIndex, lessonIndex) => {
    setActiveModule(moduleIndex);
    setActiveLesson(lessonIndex);

    // Get lesson ID and user ID
    const lesson = courseData?.modules?.[moduleIndex]?.lessons?.[lessonIndex];
    if (lesson) {
      try {
        // Get userId from localStorage (assuming it's stored during login)
        const userId = localStorage.getItem('userId') || 1; // Default to 1 for testing
        
        await fetch(`${BASE_URL}/progress/lesson/start`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            [TOKEN_HEADER]: generateToken(),
          },
          body: JSON.stringify({
            userId: parseInt(userId),
            lessonId: lesson.id
          })
        });
      } catch (error) {
        console.error('Error starting lesson:', error);
        // Don't block user experience if tracking fails
      }
    }
  };

  const handleMarkComplete = async () => {
    const lessonKey = `${activeModule}-${activeLesson}`;
    setCompletedLessons(prev => new Set([...prev, lessonKey]));

    // Get current lesson and call API to mark complete
    const lesson = courseData?.modules?.[activeModule]?.lessons?.[activeLesson];
    if (lesson) {
      try {
        const userId = localStorage.getItem('userId') || 1;
        
        await fetch(`${BASE_URL}/progress/lesson/${lesson.id}/complete`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            [TOKEN_HEADER]: generateToken(),
          },
          body: JSON.stringify({
            userId: parseInt(userId)
          })
        });
      } catch (error) {
        console.error('Error marking lesson complete:', error);
        // Don't block user experience if tracking fails
      }
    }
  };

  const handleNextLesson = () => {
    if (!courseData?.modules) return;
    const currentModuleLessons = courseData.modules[activeModule]?.lessons || [];
    
    if (activeLesson < currentModuleLessons.length - 1) {
      // Next lesson in current module
      setActiveLesson(activeLesson + 1);
    } else if (activeModule < courseData.modules.length - 1) {
      // First lesson of next module
      setActiveModule(activeModule + 1);
      setActiveLesson(0);
    }
  };

  const handlePreviousLesson = () => {
    if (!courseData?.modules) return;
    if (activeLesson > 0) {
      // Previous lesson in current module
      setActiveLesson(activeLesson - 1);
    } else if (activeModule > 0) {
      // Last lesson of previous module
      setActiveModule(activeModule - 1);
      setActiveLesson((courseData.modules[activeModule - 1]?.lessons?.length || 1) - 1);
    }
  };

  const isLessonCompleted = (moduleIndex, lessonIndex) => {
    return completedLessons.has(`${moduleIndex}-${lessonIndex}`);
  };

  const calculateProgress = () => {
    if (!courseData || !courseData.modules) return 0;
    const totalLessons = courseData.modules.reduce((sum, module) => {
      if (!module.lessonsLoaded) return sum;
      return sum + (module.lessons?.length || 0);
    }, 0);
    const completedCount = completedLessons.size;
    return totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0;
  };

  const handleLogout = () => {
    // Clear user data from localStorage
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    // Navigate to login page
    navigate('/login');
  };

  const handleSettings = () => {
    setIsProfileMenuOpen(false);
    // Navigate to settings page or open settings modal
    navigate('/settings');
  };

  const getUserName = () => {
    return localStorage.getItem('userName') || 'User';
  };

  const getUserEmail = () => {
    return localStorage.getItem('userEmail') || 'user@example.com';
  };

  const isFirstLesson = activeModule === 0 && activeLesson === 0;
  const isLastLesson = courseData?.modules 
    ? activeModule === courseData.modules.length - 1 && 
      activeLesson === (courseData.modules[activeModule]?.lessons?.length || 1) - 1
    : false;

  if (loading) {
    return (
      <div className="player-container">
        <div className="player-loading">
          <div className="spinner-large"></div>
          <p>Loading course content...</p>
        </div>
      </div>
    );
  }

  if (error || !courseData) {
    return (
      <div className="player-container">
        <div className="player-error">
          <span className="error-icon-large">⚠️</span>
          <h2>Failed to Load Course</h2>
          <p>{error || 'Course data not available'}</p>
          <button className="back-btn-error" onClick={() => navigate('/lms')}>
            ← Back to My Courses
          </button>
        </div>
      </div>
    );
  }

  // Safe access to current module and lesson after data is loaded
  const currentModule = courseData?.modules?.[activeModule];
  const currentLesson = currentModule?.lessons?.[activeLesson];

  return (
    <>
      <Header />
      <div className="player-container">
        <SEO 
          title={`${courseData.title} - Course Player | Lead with Tech`}
          description="Learn and watch course videos with our interactive player"
          keywords="course player, video learning, online courses"
        />

        {/* Top Navigation Bar */}
        <div className="player-header">
          <button className="back-btn" onClick={() => navigate('/lms')}>
            ← Back to Courses
          </button>
          <div className="course-info-header">
            <h1>{courseData.title}</h1>
            <span className="course-code-badge">{courseData.code}</span>
          </div>
          <div className="user-profile" ref={profileMenuRef}>
            <button 
              className="profile-button" 
              onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
              aria-label="User profile menu"
            >
              <span className="profile-avatar">{getUserName().charAt(0).toUpperCase()}</span>
              <span className="profile-name">{getUserName()}</span>
              <span className="profile-dropdown-icon">{isProfileMenuOpen ? '▲' : '▼'}</span>
            </button>
            {isProfileMenuOpen && (
              <div className="profile-dropdown-menu">
                <div className="profile-dropdown-header">
                  <div className="profile-dropdown-avatar">{getUserName().charAt(0).toUpperCase()}</div>
                  <div className="profile-dropdown-info">
                    <div className="profile-dropdown-name">{getUserName()}</div>
                    <div className="profile-dropdown-email">{getUserEmail()}</div>
                  </div>
                </div>
                <div className="profile-dropdown-divider"></div>
                <button className="profile-dropdown-item" onClick={handleSettings}>
                  <span className="profile-dropdown-icon">⚙️</span>
                  Settings
                </button>
                <button className="profile-dropdown-item logout" onClick={handleLogout}>
                  <span className="profile-dropdown-icon">🚪</span>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Progress Bar Section */}
        <div className="progress-section">
          <div className="progress-info">
            <span className="progress-text">{calculateProgress()}% Complete</span>
            <div className="progress-bar-full">
              <div className="progress-fill-full" style={{ width: `${calculateProgress()}%` }}></div>
            </div>
          </div>
        </div>

      <div className="player-main">
        {/* Reopen Sidebar Button */}
        {sidebarCollapsed && (
          <button 
            className="reopen-sidebar-btn" 
            onClick={() => setSidebarCollapsed(false)}
            title="Open Course Content"
          >
            <span className="reopen-icon">☰</span>
            <span className="reopen-text">Course Curriculum</span>
          </button>
        )}

        {/* Sidebar - Course Content */}
        <div className={`player-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <h3>Course Content</h3>
            <button 
              className="collapse-btn" 
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              title={sidebarCollapsed ? "Expand" : "Collapse"}
            >
              {sidebarCollapsed ? '→' : '←'}
            </button>
          </div>

          <div className="modules-list">
            {courseData.modules.map((module, moduleIndex) => {
              const isExpanded = expandedModules.has(moduleIndex);
              const isLoading = loadingModules.has(moduleIndex);
              
              return (
                <div key={module.id} className="module-item">
                  <button
                    className="module-header"
                    onClick={() => handleModuleToggle(moduleIndex)}
                  >
                    <span className="module-toggle-icon">
                      {isExpanded ? '▼' : '▶'}
                    </span>
                    <span className="module-icon">📚</span>
                    <div className="module-info">
                      <h4>Module {moduleIndex + 1}: {module.title}</h4>
                      <span className="module-duration">{module.duration}</span>
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div className="lessons-list">
                      {isLoading ? (
                        <div className="module-loading">
                          <div className="spinner-small"></div>
                          <span>Loading lessons...</span>
                        </div>
                      ) : module.lessons && module.lessons.length > 0 ? (
                        module.lessons.map((lesson, lessonIndex) => (
                          <button
                            key={lesson.id}
                            className={`lesson-item ${
                              activeModule === moduleIndex && activeLesson === lessonIndex ? 'active' : ''
                            } ${isLessonCompleted(moduleIndex, lessonIndex) ? 'completed' : ''}`}
                            onClick={() => handleLessonClick(moduleIndex, lessonIndex)}
                          >
                            <span className="lesson-number">{lessonIndex + 1}</span>
                            <div className="lesson-details">
                              <span className="lesson-title">{lesson.title}</span>
                              <span className="lesson-duration">⏱️ {lesson.duration_minutes ? `${lesson.duration_minutes} min` : lesson.duration || 'N/A'}</span>
                            </div>
                            {isLessonCompleted(moduleIndex, lessonIndex) && (
                              <span className="completed-icon">✓</span>
                            )}
                          </button>
                        ))
                      ) : (
                        <div className="no-lessons">
                          <span>No lessons available</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Main Content Area - Video Player */}
        <div className="player-content">
          {/* Lesson Title Header */}
          <div className="lesson-title-header">
            <div className="lesson-title-info">
              <h2>{currentLesson?.title || 'Select a lesson to begin'}</h2>
              {currentLesson?.description && (
                <p className="lesson-description">{currentLesson.description}</p>
              )}
            </div>
            {currentLesson && (
              <button 
                className={`mark-complete-btn ${isLessonCompleted(activeModule, activeLesson) ? 'completed' : ''}`}
                onClick={handleMarkComplete}
                disabled={isLessonCompleted(activeModule, activeLesson)}
              >
                {isLessonCompleted(activeModule, activeLesson) ? '✓ Completed' : 'Mark as Complete'}
              </button>
            )}
          </div>

          <div className="video-container">
            {currentLesson?.content_type === 'video' && currentLesson.video_url ? (
              <iframe
                src={currentLesson.video_url}
                title={currentLesson.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="video-player"
              ></iframe>
            ) : currentLesson?.video_url ? (
              <iframe
                src={currentLesson.video_url}
                title={currentLesson.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="video-player"
              ></iframe>
            ) : (
              <div className="video-placeholder">
                <span className="placeholder-icon">🎥</span>
                <p>No video available for this lesson</p>
              </div>
            )}
          </div>

          {/* Additional Resources/Notes Section */}
          <div className="additional-content">
            <div className="content-tabs">
              <button className="tab-btn active">📝 Notes</button>
            </div>
            <div className="tab-content">
              <div className="notes-section">
                <h3>Lesson Notes</h3>
                <p>Take notes while watching the video. Your notes are automatically saved, viewed by mentor to clarify questions.</p>
                <textarea 
                  className="notes-textarea" 
                  placeholder="Start typing your notes here..."
                  rows="6"
                ></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Footer />
    </>
  );
}

export default Player;
